import { boot } from 'quasar/wrappers'
import { App as CapacitorApp } from '@capacitor/app'
import { getRjmStateChangeObj } from '../stores/saasUserManagementClientStore'
import saasApiClientServerRequestQueue from '../saasApiClientServerRequestQueue'
import { useUserManagementClientStoreStore } from 'stores/saasUserManagementClientStore'
import { useUserDetailsStore } from 'stores/userDetailsStore'
import { useChatStore } from 'stores/chatStore'
import mainLayoutCommon from '../layouts/common.js'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extracts the path and remaining query params from a custom-scheme deep link.
 * Returns null if the URL is malformed or the path is missing / invalid.
 *
 * The `route` param is removed from queryParams so it doesn't appear twice
 * on the destination route; all other params (including jwtretervialtoken) are
 * preserved for the destination page.
 *
 * @param {string} url  Raw URL from CapacitorApp appUrlOpen event
 * @returns {{ routePath: string, queryParams: Record<string,string> } | null}
 */
function parseLoginRedirectUrl (url) {
  let parsed
  try {
    parsed = new URL(url)
  } catch {
    console.error('[deeplink] Failed to parse deep link URL:', url)
    return null
  }

  const routePath = parsed.searchParams.get('route')
  if (!routePath || !routePath.startsWith('/')) {
    console.error('[deeplink] Deep link missing or invalid "route" param:', url)
    return null
  }

  const queryParams = Object.fromEntries(parsed.searchParams.entries())
  delete queryParams.route

  return { routePath, queryParams }
}

/**
 * Handles the JWT retrieval flow once the router has navigated to the target
 * route.  Called only when router.currentRoute.value.path === routePath.
 *
 * @param {{ jwtretervialtoken: string, routePath: string, app: object, router: object }} opts
 */
function processJwtRetrievalOnCurrentRoute ({ jwtretervialtoken, routePath, app, router }) {
  if (!jwtretervialtoken) {
    console.error('[deeplink] Login redirect received but jwtretervialtoken is absent')
    return
  }

  const rjmStateChange = getRjmStateChangeObj()

  const callback = {
    ok () {
      const tenantName = router.currentRoute.value.params.tenantName
      const userManagementClientStore = useUserManagementClientStoreStore()
      const userDetailsStore = useUserDetailsStore()
      const chatStore = useChatStore()

      mainLayoutCommon.refreshAllStores({
        bus: app.config.globalProperties.$bus,
        Notify: undefined,
        userManagementClientStore,
        userDetailsStore,
        router,
        tenantName,
        chatStore,
        matrix: app.config.globalProperties.$matrix
      })
    },
    error (response) {
      console.error('[deeplink] processRecievedJWTretervialtoken failed:', response)
      // TODO: redirect to login or surface an error notification to the user
    }
  }

  rjmStateChange.executeAction('processRecievedJWTretervialtoken', {
    jwtretervialtoken,
    callback,
    curpath: routePath,
    startAllBackendCallQueuesFn: saasApiClientServerRequestQueue.getStartAllBackendCallQueuesFn({ curpath: routePath })
  })
}

/**
 * Handles a custom-scheme login-redirect deep link:
 *   app.socialclubhub.loginredirect://return?route=/some/path&jwtretervialtoken=xxx
 *
 * Two cases:
 *  A) Router is already on the target route  →  process the JWT inline.
 *  B) Router is elsewhere (e.g. cold start)  →  navigate first; the
 *     destination page is expected to handle jwtretervialtoken from queryParams.
 *
 * @param {string} url
 * @param {object} app     Vue app instance
 * @param {object} router  Vue Router instance
 */
function handleLoginRedirectLink (url, app, router) {
  const parsed = parseLoginRedirectUrl(url)
  if (!parsed) return

  const { routePath, queryParams } = parsed

  const alreadyOnRoute = router.currentRoute.value.path === routePath

  if (alreadyOnRoute) {
    processJwtRetrievalOnCurrentRoute({
      jwtretervialtoken: queryParams.jwtretervialtoken,
      routePath,
      app,
      router
    })
  } else {
    // Navigate to the route; jwtretervialtoken remains in queryParams so the
    // destination page can pick it up.
    router.replace({ path: routePath, query: queryParams })
  }
}

/**
 * Handles a standard HTTPS deep link by pushing the URL path into the router.
 *
 * @param {string} url
 * @param {object} router
 */
function handleHttpsLink (url, router) {
  let parsed
  try {
    parsed = new URL(url)
  } catch (err) {
    console.error('[deeplink] Invalid HTTPS deep link URL:', url, err)
    return
  }
  router.push(parsed.pathname)
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

export default boot(async ({ app, router }) => {
  CapacitorApp.addListener('appUrlOpen', (event) => {
    const url = event.url
    if (!url) return

    if (url.startsWith('app.socialclubhub.loginredirect://')) {
      handleLoginRedirectLink(url, app, router)
      return
    }

    handleHttpsLink(url, router)
  })
})
